import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ChevronDown, ChevronRight, FolderTree, Pencil, Plus, Tags, Trash2 } from "lucide-react";
import ConfirmationModal from "../../../components/ConfirmationModal";
import CategoryModal from "./CategoryModal";
import { searchCategories, deleteCategory, getBrandsByCategory } from "../../../services/materialAdmin";

const TYPE_BADGE = {
  text: "bg-slate-100 text-slate-600",
  number: "bg-sky-50 text-sky-700",
  select: "bg-violet-50 text-violet-700",
  multiselect: "bg-fuchsia-50 text-fuchsia-700",
};

const CategoriesTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [detail, setDetail] = useState({}); // categoryId -> {brands, loading}

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null); // {id, name}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await searchCategories({ isActive: null });
      setRows(res?.items || []);
    } catch (e) { toast.error("Failed to load categories"); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (categoryId) => {
    setDetail((d) => ({ ...d, [categoryId]: { ...(d[categoryId] || {}), loading: true } }));
    try {
      const brands = await getBrandsByCategory(categoryId).catch(() => []);
      setDetail((d) => ({ ...d, [categoryId]: { brands, loading: false } }));
    } catch (e) {
      setDetail((d) => ({ ...d, [categoryId]: { brands: [], loading: false } }));
    }
  }, []);

  const toggleExpand = (categoryId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else { next.add(categoryId); loadDetail(categoryId); }
      return next;
    });
  };

  const doDelete = async () => {
    const { id } = confirm;
    setConfirm(null);
    try {
      await deleteCategory(id);
      toast.success("Category deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete category");
    }
  };

  const categoryOptions = rows.map((c) => ({ value: c.materialCategoryId, label: c.categoryName }));
  const parentName = (id) => rows.find((c) => c.materialCategoryId === id)?.categoryName;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">The material taxonomy — top-level categories and their sub-categories (a sub-category has a parent). Vendors are matched to work by the sub-categories they serve.</p>
        <button onClick={() => { setEditing(null); setModalOpen(true); }}
          className="inline-flex items-center gap-2 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          <Plus size={16} /> Add Category
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center">
          <FolderTree size={28} className="mx-auto text-slate-300" />
          <p className="text-base font-semibold text-slate-900 mt-2">No categories yet</p>
          <p className="text-sm text-slate-500 mt-1">Create the first category to start building the taxonomy.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((c) => {
            const isOpen = expanded.has(c.materialCategoryId);
            const d = detail[c.materialCategoryId] || {};
            return (
              <div key={c.materialCategoryId} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                {/* Row header */}
                <div className="flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-slate-50/70"
                  onClick={() => toggleExpand(c.materialCategoryId)}>
                  <span className="text-slate-400">{isOpen ? <ChevronDown size={17} /> : <ChevronRight size={17} />}</span>
                  <div className="w-9 h-9 rounded-xl bg-flamingo-50 text-flamingo-600 flex items-center justify-center shrink-0">
                    <FolderTree size={17} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900 truncate">{c.categoryName}</p>
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-mono">{c.categoryCode}</span>
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${c.parentCategoryId ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                        {c.parentCategoryId ? "SUB-CATEGORY" : "CATEGORY"}
                      </span>
                      {!c.isActive && (
                        <span className="px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-[11px] font-semibold">INACTIVE</span>
                      )}
                      {c.isPendingReview && (
                        <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-semibold">PENDING REVIEW</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      Unit: {c.defaultUnit}
                      {c.parentCategoryId ? ` · under ${parentName(c.parentCategoryId) || "…"}` : ""}
                      {c.description ? ` · ${c.description}` : ""}
                    </p>
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-600">
                    <Tags size={12} /> {(c.attributes || []).length} attributes
                  </span>
                  <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <button title="Edit" onClick={() => { setEditing(c); setModalOpen(true); }}
                      className="w-8 h-8 rounded-md text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 inline-flex items-center justify-center">
                      <Pencil size={15} />
                    </button>
                    <button title="Delete" onClick={() => setConfirm({ id: c.materialCategoryId, name: c.categoryName })}
                      className="w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Attributes */}
                    <div className="rounded-xl bg-white border border-slate-200 p-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2.5 flex items-center gap-1.5"><Tags size={13} /> Attributes</p>
                      {(c.attributes || []).length === 0 ? (
                        <p className="text-xs text-slate-400">None — edit the category to define attributes.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(c.attributes || []).map((a) => (
                            <div key={a.materialCategoryAttributeId} className="flex items-center gap-2 text-sm">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${TYPE_BADGE[a.attributeType] || TYPE_BADGE.text}`}>{a.attributeType}</span>
                              <span className="text-slate-700 truncate">{a.attributeLabel}{a.unit ? ` (${a.unit})` : ""}</span>
                              {a.isRequired && <span className="text-flamingo-600 text-[11px] font-semibold">required</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Linked brands */}
                    <div className="rounded-xl bg-white border border-slate-200 p-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2.5">Linked Brands</p>
                      {d.loading ? (
                        <p className="text-xs text-slate-400">Loading…</p>
                      ) : (d.brands || []).length === 0 ? (
                        <p className="text-xs text-slate-400">No brands linked — manage links in the Brands tab.</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {(d.brands || []).map((b) => (
                            <span key={b.materialBrandId} className="px-2 py-1 rounded-lg bg-flamingo-50 text-flamingo-700 border border-flamingo-100 text-[11px] font-medium">{b.brandName}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CategoryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        editing={editing}
        categories={categoryOptions}
      />

      <ConfirmationModal
        isOpen={!!confirm}
        onConfirm={doDelete}
        onCancel={() => setConfirm(null)}
        message={`Delete category "${confirm?.name}"? Blocked if it has sub-categories.`}
      />
    </div>
  );
};

export default CategoriesTab;
